import React, { useEffect, useState } from 'react';
import PageContentWrapper from 'src/components/PageContentWrapper/PageContentWrapper';
import { CreateProjectStepsTitle } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStepsTitle/CreateProjectStepsTitle';
import { Button } from 'src/components/ui/button';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector } from 'src/store';
import { ProductsType } from 'src/constants/products';
import { PATH_DASHBOARD } from 'src/constants/spa-routes';
import {
  useGetAllConfigurationSettingsQuery,
  useGetConfigurationSettingsPromptQuery,
  useGetDefaultConfigurationTemplateFieldsQuery,
  useUpdateConfigurationSettingsPromptMutation,
} from 'src/services';
import moment from 'moment';
import { useLoader } from 'src/provider/LoaderProvider';
import { useDispatch } from 'react-redux';
import { setCreationFlow } from 'src/store/slices/creationFlowSlices';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { booleanScheme, stringScheme } from 'src/utils/validation-schemes';
import { CreateProjectHead } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectHead/CreateProjectHead';
import { Skeleton } from 'src/components/ui/skeleton';
import * as yup from 'yup';
import { ChannelAccordion } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep5/ChannelAccordion/ChannelAccordion';
import { StyleAccordion } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep5/StyleAccordion/StyleAccordion';
import { ResponseLengthAccordion } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep5/ResponseLengthAccordion/ResponseLengthAccordion';
import { API_VERSION } from 'src/constants/api-routes';

const LoadingSkeleton = () => {
  return (
    <div className={'w-[455px] flex flex-col gap-5'}>
      <Skeleton className={'w-full h-4 mb-10'} />
      <Skeleton className={'w-full h-20'} />
      <Skeleton className={'w-full h-96'} />
    </div>
  );
};

const CreateProjectStep5Page = () => {
  const [settingsPage, setSettingsPage] = useState(1);
  const [settings, setSettings] = useState(null);
  const [activeSettings, setActiveSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [inputData, setInputData] = useState(null);
  const [formFields, setFormFields] = useState(null);
  const [formFieldsObject, setFormFieldsObject] = useState<Record<string, any>>({});
  const [accordionValue, setAccordionValue] = useState('');
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const { id, step } = useParams();
  const loader = useLoader();
  const navigate = useNavigate();
  const { projectType } = useAppSelector((s) => s.projectsSlices);
  const projectTypeVar = projectType || type;
  const creationFlowState = useAppSelector((s) => s.creationFlowSlices);
  const dispatch = useDispatch();
  const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;
  const isV2 = version === API_VERSION.V2;
  const { data: defaultTemplateFields, isLoading: isLoadingDefaultTemplateFields } = (
    useGetDefaultConfigurationTemplateFieldsQuery as any
  )();
  const { data: allConfigurationSettings, isLoading: isLoadingAllConfigurationSettings } =
    useGetAllConfigurationSettingsQuery(
      {
        id: id,
        page: settingsPage,
      },
      { skip: !Boolean(id && defaultTemplateFields), refetchOnMountOrArgChange: true },
    );
  const [
    updateConfigurationSettings,
    { data: updateConfigurationSettingsData, isLoading: isLoadingUpdateConfigurationSettings },
  ] = useUpdateConfigurationSettingsPromptMutation();
  const { data: configurationSettingsPrompt, isLoading: isLoadingConfigurationSettingsPrompt } =
    useGetConfigurationSettingsPromptQuery(
      { project_id: id, settings_id: activeSettings && activeSettings.id },
      {
        skip: !Boolean(id && activeSettings && defaultTemplateFields),
        refetchOnMountOrArgChange: true,
      },
    );

  useEffect(() => {
    if (
      isLoadingDefaultTemplateFields ||
      isLoadingAllConfigurationSettings ||
      isLoadingConfigurationSettingsPrompt ||
      isLoadingUpdateConfigurationSettings ||
      !configurationSettingsPrompt ||
      settings === null
    ) {
      setIsLoading(true);
      loader.show();
    } else {
      setIsLoading(false);
      loader.hide();
    }
  }, [
    isLoadingAllConfigurationSettings,
    isLoadingDefaultTemplateFields,
    isLoadingConfigurationSettingsPrompt,
    isLoadingUpdateConfigurationSettings,
    configurationSettingsPrompt,
    settings,
  ]);

  useEffect(() => {
    if (defaultTemplateFields) {
      const fields = (defaultTemplateFields as any).form_fields.map((field, index) => ({
        ...field,
        index,
      }));
      setFormFields(fields);
      setInputData(null);
    }
  }, [defaultTemplateFields]);

  useEffect(() => {
    if (formFields) {
      const fieldsObject = formFields.reduce((prev, current) => {
        prev[current.key] = current;

        return prev;
      }, {});

      setFormFieldsObject(fieldsObject);
    }
  }, [formFields]);

  useEffect(() => {
    if (settings) {
      const config = creationFlowState[id];
      const settingsId = config.promptBuilderSettingsId;

      if (settingsId && settings.length) {
        const setting = settings.find((setting) => `${setting.id}` === `${settingsId}`);

        setting && setActiveSettings(setting);
      }
    }
  }, [settings, creationFlowState]);

  useEffect(() => {
    if (configurationSettingsPrompt) {
      const fields = (configurationSettingsPrompt as any).form_fields.map((field, index) => ({
        ...field,
        index,
      }));

      setFormFields(fields);
      setInputData((configurationSettingsPrompt as any).input_data);
    }
  }, [configurationSettingsPrompt]);

  useEffect(() => {
    if (allConfigurationSettings) {
      setSettings((prevState) => {
        const prev = prevState || [];
        const newState = [...prev];

        (allConfigurationSettings as any).items.forEach((item) => {
          const index = prev.findIndex((prevItem) => {
            return prevItem.id === item.id;
          });

          if (index > -1) {
            newState[index] = item;
          } else {
            newState.push(item);
          }
        });

        newState.sort((a, b) => +moment(b.created_at) - +moment(a.created_at));

        return newState;
      });

      if ((allConfigurationSettings as any).page_info.has_next_page) {
        setSettingsPage((allConfigurationSettings as any).page_info.page + 1);
      }
    }
  }, [allConfigurationSettings]);

  useEffect(() => {
    if (inputData) {
      methods.reset({
        pf_tone_of_voice_channel:
          inputData?.pf_tone_of_voice_channel ||
          formFieldsObject?.pf_tone_of_voice_channel?.default_value?.value ||
          'chat',
        pf_tone_of_voice_style:
          inputData?.pf_tone_of_voice_style ||
          formFieldsObject?.pf_tone_of_voice_style?.default_value?.value ||
          'casual',
        pf_tone_of_voice_resp_len:
          inputData?.pf_tone_of_voice_resp_len ||
          formFieldsObject?.pf_tone_of_voice_resp_len?.default_value?.value ||
          'concise',
        pf_tone_of_voice_style_custom: inputData?.pf_tone_of_voice_style_custom || '',
        pf_tone_of_voice_emoji:
          inputData?.pf_tone_of_voice_emoji ||
          formFieldsObject?.pf_tone_of_voice_emoji?.default_value?.value ||
          false,
      });
    }
  }, [inputData]);

  useEffect(() => {
    if (updateConfigurationSettingsData) {
      next();
    }
  }, [updateConfigurationSettingsData]);

  useEffect(() => {
    const config = creationFlowState[id];

    if (config && +config.step < +step) {
      dispatch(
        setCreationFlow({
          id: id,
          step: step,
        }),
      );
    }
  }, []);

  const methods = useForm<any>({
    defaultValues: {
      pf_tone_of_voice_channel:
        inputData?.pf_tone_of_voice_channel ||
        formFieldsObject?.pf_tone_of_voice_channel?.default_value?.value ||
        'chat',
      pf_tone_of_voice_style:
        inputData?.pf_tone_of_voice_style ||
        formFieldsObject?.pf_tone_of_voice_style?.default_value?.value ||
        'casual',
      pf_tone_of_voice_resp_len:
        inputData?.pf_tone_of_voice_resp_len ||
        formFieldsObject?.pf_tone_of_voice_resp_len?.default_value?.value ||
        'concise',
      pf_tone_of_voice_style_custom: inputData?.pf_tone_of_voice_style_custom || '',
      pf_tone_of_voice_emoji:
        typeof inputData?.pf_tone_of_voice_emoji !== 'undefined'
          ? inputData?.pf_tone_of_voice_emoji
          : formFieldsObject?.pf_tone_of_voice_emoji?.default_value?.value || false,
    },
    resolver: yupResolver(
      yup.lazy((values) => {
        const baseSchema = {
          pf_tone_of_voice_emoji: booleanScheme(),
          pf_tone_of_voice_channel: stringScheme({
            required: formFieldsObject?.pf_tone_of_voice_channel?.is_required || false,
          }).label('Tone of Voice Channel'),
          pf_tone_of_voice_style: stringScheme({
            required: formFieldsObject?.pf_tone_of_voice_style?.is_required || false,
          }).label('Tone of Voice Style'),
          pf_tone_of_voice_style_custom: stringScheme({
            required:
              formFieldsObject?.pf_tone_of_voice_style_custom?.is_required ||
              values.pf_tone_of_voice_style === 'custom',
          }).label('Tone of Voice Style - Custom'),
        };

        return yup.object(baseSchema);
      }),
    ),
  });

  const style = methods.watch('pf_tone_of_voice_style');

  const submit = (data) => {
    if (activeSettings) {
      const body = {
        input_data: {
          ...inputData,
          ...data,
          pf_tone_of_voice_emoji: ['casual', 'custom'].includes(style)
            ? data.pf_tone_of_voice_emoji
            : false,
          pf_tone_of_voice_style_custom:
            style === 'custom' ? data.pf_tone_of_voice_style_custom : null,
        },
      };

      updateConfigurationSettings({ project_id: id, settings_id: activeSettings.id, body });
    }
  };

  const next = () => {
    navigate(
      projectTypeVar === ProductsType.customer
        ? `${PATH_DASHBOARD.PROJECTS}/${id}/new/${+step + 1}?type=${projectTypeVar}`
        : `${PATH_DASHBOARD.PROJECTS}/${id}?type=${projectTypeVar}`,
      { replace: true },
    );
  };

  const back = () => {
    navigate(
      projectTypeVar === ProductsType.customer
        ? `${PATH_DASHBOARD.PROJECTS}/${id}/new/${+step - 1}?type=${projectTypeVar}`
        : `${PATH_DASHBOARD.PROJECTS}/${id}?type=${projectTypeVar}`,
      { replace: true },
    );
  };

  return (
    <React.Fragment>
      <PageContentWrapper
        menuBarVisible={false}
        className={'create-project-step items-center !gap-5'}
      >
        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <CreateProjectStepsTitle step={isV2 ? +step - 1 : +step} title={'Tone of Voice'} />
            <CreateProjectHead step={+step} titleIndex={5} />
            <div className={'flex flex-col items-center w-[455px] gap-5'}>
              <FormProvider {...methods}>
                <form
                  id={'create-project-step-5'}
                  className={'flex flex-col gap-5 w-full'}
                  onSubmit={methods.handleSubmit(submit)}
                >
                  <ChannelAccordion
                    accordionValue={accordionValue}
                    setAccordionValue={setAccordionValue}
                    options={formFieldsObject?.pf_tone_of_voice_channel?.options}
                  />
                  <StyleAccordion
                    accordionValue={accordionValue}
                    setAccordionValue={setAccordionValue}
                    options={formFieldsObject?.pf_tone_of_voice_style?.options}
                  />
                  <ResponseLengthAccordion
                    accordionValue={accordionValue}
                    setAccordionValue={setAccordionValue}
                    options={formFieldsObject?.pf_tone_of_voice_resp_len?.options}
                  />
                </form>
              </FormProvider>
              <Button
                form="create-project-step-5"
                type="submit"
                className="min-w-24 w-full flex flex-row h-12 mt-5"
              >
                Continue
              </Button>
              <div
                onClick={back}
                className={'text-[#3F60B3] text-xs font-medium cursor-pointer hover:underline'}
              >
                ← Back
              </div>
            </div>
          </>
        )}
      </PageContentWrapper>
    </React.Fragment>
  );
};

export default CreateProjectStep5Page;
